import { safeApiCall } from "api/common.service";
import { getAdminOrderById } from "api/order.service";
import {
  formatDate,
  formatNZD,
  formatPercent,
  sanitizeText,
} from "common/utils";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";
import React, { useEffect, useRef, useState } from "react";
import { DebtorSummary, OrderDataEntryForm } from "types/model.types";

interface IAdminOrderPreviewProps {
  id: number;
  orderStage: number;
  invoiceTo: DebtorSummary | null;
  deliverTo: DebtorSummary | null;
}
const AdminOrderPreview: React.FC<IAdminOrderPreviewProps> = ({
  id,
  orderStage,
  invoiceTo,
  deliverTo,
}) => {
  const apiFetchRef = useRef<boolean>(false);
  const [initialData, setInitialData] = useState<OrderDataEntryForm | null>(
    null,
  );
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
  } = useProgressiveDebtorFetch();
  const [selectedDebtor, setSelectedDebtor] = useState<DebtorSummary | null>(
    invoiceTo,
  );
  const [selectedDeliverToDebtor, setSelectedDeliverToDebtor] =
    useState<DebtorSummary | null>(deliverTo);

  useEffect(() => {
    if (!id) return;

    if (apiFetchRef.current) return;
    apiFetchRef.current = true;

    const fetchInitialData = async () => {
      const response = await safeApiCall(getAdminOrderById(id));
      apiFetchRef.current = false;

      if (response) {
        setInitialData(response);
      }
    };

    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (debtors.length === 0 || !id || !initialData) return;

    // Only set if not already provided (landing page case)
    if (selectedDebtor === null) {
      const debtor =
        debtors.find((d) => d.id === initialData.summary.invoiceToDebtorId) ??
        null;
      setSelectedDebtor(debtor);
    }

    if (selectedDeliverToDebtor === null) {
      const debtor =
        debtors.find((d) => d.id === initialData.summary.deliverToDebtorId) ??
        null;
      setSelectedDeliverToDebtor(debtor);
    }
  }, [id, debtors, initialData]);

  // assuming initialData?.entries is an array of objects like { total: number, gst: number }
  // const totals = initialData?.entries?.reduce(
  //   (acc, item) => {
  //     acc.total += item.total || 0;
  //     acc.gst += item.gst || 0;
  //     return acc;
  //   },
  //   { total: 0, gst: 0 },
  // );

  return (
    <>
      <div className="sub-title">
        {"*** "}
        {orderStage === 0
          ? "Picking List"
          : orderStage === 1
            ? "Packing Slip"
            : "Invoice"}
        {" ***"}
      </div>
      <table className="print-table padTop">
        <tbody>
          <tr>
            <td className="leftCol">
              <span className="rcl">Date Ordered:</span>
              <span className="rcv">
                {formatDate(
                  initialData?.summary.createdOn ?? new Date().toDateString(),
                )}
              </span>
            </td>
            <td className="rightCol">
              <div>
                <span className="rcl">
                  {orderStage === 0
                    ? "ORDER NO"
                    : orderStage === 1
                      ? "P/SLIP NO"
                      : "TAX INVOICE NO"}
                  :
                </span>
                <span className="rcv">
                  {orderStage === 0
                    ? initialData?.summary.id
                    : initialData?.summary.invoiceId}
                </span>
              </div>
              <div>
                <span className="rcl">CUSTOMER NO:</span>
                <span className="rcv">
                  {initialData?.summary.invoiceToDebtorId}
                </span>
              </div>
              <div>
                <span className="rcl"> GST:</span>
                <span className="rcv">
                  {process.env.REACT_APP_GST_NO ?? "20-898-763"}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <table className="print-table padTop">
        <tbody>
          <tr className="vat" key="cns1r1">
            <td style={{ width: "50%" }} className="leftCol">
              <span className="heading">Invoice To</span>
              <div className="divider" />
            </td>
            <td style={{ width: "50%" }}>
              <span className="heading">Deliver To</span>
              <div className="divider" />
            </td>
          </tr>
          <tr className="vat" key="cns1r2">
            <td>
              <div>
                {selectedDebtor?.name}
                {(selectedDebtor?.primaryAddress ?? "")
                  .split(/\r?\n/)
                  .map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
              </div>
            </td>
            <td>
              <div>
                {selectedDeliverToDebtor?.name}
                {(
                  selectedDeliverToDebtor?.deliveryAddress ??
                  selectedDeliverToDebtor?.primaryAddress ??
                  ""
                )
                  .split(/\r?\n/)
                  .map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="spacer padTop padBottom" />

      <table className="print-table">
        <tbody>
          <tr>
            <td>
              <span className="bold">Your Order No: </span>
              <span>{initialData?.summary.yourOrderNo ?? ""}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="spacer padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Product</th>
            <th
              className={`${orderStage === 0 ? "description description2" : "description"}`}
            >
              Description
            </th>
            <th className="money xs">Ord</th>
            <th className="money xs">Sup</th>
            {orderStage === 0 && <th className="wrap">Ref</th>}
            {orderStage !== 0 && (
              <>
                <th className="money">E/Price</th>
                <th className="money">C/Price</th>
                <th className="money">Disc %</th>
                <th className="money">Total</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          <tr key="cns2r1">
            <td></td>
            <td className="wrap" colSpan={orderStage === 0 ? 4 : 7}>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(initialData?.summary.notes ?? ""),
                }}
              />
            </td>
          </tr>
          {initialData?.entries.map((item, idx) => (
            <tr
              key={`prv-${idx}`}
              className={`${(item.onHand ?? 0) === 0 ? "no-stock" : (item.isBackOrderItem ?? false) ? "back-order" : ""}`}
            >
              <td className="nowrap">{`${item.productId.toString()}${item.productCode.toUpperCase()}`}</td>
              <td
                className={`${orderStage === 0 ? "description description2" : "description"}`}
              >
                {item.description}
              </td>
              <td className="money xs">{item.ordered ?? 0}</td>
              <td className="money xs">
                {(item.supplied ?? 0) > 0
                  ? item.supplied
                  : orderStage === 0
                    ? ""
                    : (item.supplied ?? 0)}
              </td>
              {orderStage === 0 && <td className="wrap boxl">{item.ref}</td>}
              {orderStage !== 0 && (
                <>
                  <td className="money">{formatNZD(item.eachPrice)}</td>
                  <td className="money">{formatNZD(item.cartonPrice)}</td>
                  <td className="money">
                    {item.discount === null
                      ? ""
                      : item.discount > 0
                        ? formatPercent(item.discount, 0, true)
                        : ""}
                  </td>
                  <td className="money">{formatNZD(item.total)}</td>
                </>
              )}
            </tr>
          ))}
          <tr key="cns2r2">
            <td></td>
            <td className="wrap" colSpan={orderStage === 0 ? 4 : 7}>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeText(initialData?.summary.notes2 ?? ""),
                }}
              />
            </td>
          </tr>
          {orderStage !== 0 && (
            <>
              <tr key="cns3r1">
                <td colSpan={6} />
                <td className="money">Sub Total</td>
                <td className="money">
                  {formatNZD(initialData?.summary.totalAmount ?? 0)}
                </td>
              </tr>
              <tr key="cns3r2">
                <td colSpan={6} />
                <td className="money">GST</td>
                <td className="money">
                  {formatNZD(initialData?.summary.totalGst ?? 0)}
                </td>
              </tr>
              <tr key="cns3r3">
                <td colSpan={6} />
                <td className="money money-focal">TOTAL</td>
                <td className="money money-focal">
                  {formatNZD(
                    (initialData?.summary.totalAmount ?? 0) +
                      (initialData?.summary.totalGst ?? 0),
                  )}
                </td>
              </tr>
              <tr key="cns3r4">
                <td className="disclaimer" colSpan={8}>
                  <br />
                  NOTE: THE GOODS SHOWN ON THIS INVOICE WILL CONTINUE TO REMAIN
                  THE PROPERTY OF JOHN JACOBSON LTD UNTIL THIS INVOICE HAS BEEN
                  PAID IN FULL
                </td>
              </tr>
              {orderStage === 2 && (
                <tr key="cns3r5">
                  <td colSpan={8}>
                    Direct Credit payments can be made to: <br />
                    Westpac, Petone, Account <em>03-0543-0207702-00</em>
                  </td>
                </tr>
              )}
              <tr key="cns3r6">
                <td colSpan={8}>
                  Printed on: {formatDate(new Date().toDateString())}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </>
  );
};

export default AdminOrderPreview;
